import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import Navbar from "@/components/Navbar";
import EducatorQuickShare from "@/components/EducatorQuickShare";
import CTEProfileReport from "@/components/CTEProfileReport";
import StudentJourneyMap from "@/components/StudentJourneyMap";
import StudentProfileModal from "@/components/StudentProfileModal";
import { CheckCircle2, Sparkles, Lightbulb, Rocket, ArrowRight, Pencil, Crown, Megaphone, Calculator, Cog, Download } from "lucide-react";
import LemonBotFloater from "@/components/LemonBotFloater";
import AnnouncementBanner from "@/components/AnnouncementBanner";

// Mirror the avatar/role catalogs from StudentDashboard so middle schoolers see
// the same personalization affordances. Backend remains source-of-truth.
const AVATAR_EMOJI = {
  lemon: "🍋", rocket: "🚀", star: "⭐", sparkles: "✨",
  fire: "🔥", lightning: "⚡", globe: "🌍", leaf: "🌱",
  trophy: "🏆", target: "🎯", rainbow: "🌈", music: "🎵",
};
const TEAM_ROLE_META = {
  leader: { labelKey: "teamRole.leader", icon: Crown },
  marketing: { labelKey: "teamRole.marketing", icon: Megaphone },
  finance: { labelKey: "teamRole.finance", icon: Calculator },
  operations: { labelKey: "teamRole.operations", icon: Cog },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function MiddleSchoolDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [modules, setModules] = useState([]);
  const [moduleSessions, setModuleSessions] = useState({}); // {module_id: [sessions]}
  const [progress, setProgress] = useState([]);
  const [capstoneFields, setCapstoneFields] = useState({});
  const [loading, setLoading] = useState(true);
  // ---- Personalization Phase 1 ----
  const [profile, setProfile] = useState(user);
  const [showProfileModal, setShowProfileModal] = useState(false);
  useEffect(() => { setProfile(user); }, [user]);
  const [error, setError] = useState("");

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const loadData = async () => {
    try {
      const lang = i18n.language || 'en';
      const [modulesRes, progressRes] = await Promise.all([
        axios.get(`${API}/modules?learning_path=middle_school&lang=${lang}`),
        axios.get(`${API}/progress/my`),
      ]);
      const mods = (modulesRes.data || []).sort((a, b) => a.order - b.order);
      setModules(mods);
      setProgress(progressRes.data || []);
      
      // Load sessions for every module in parallel
      const sessionsByModule = {};
      await Promise.all(mods.map(async (m) => {
        try {
          const r = await axios.get(`${API}/modules/${m.id}/lessons?lang=${lang}`);
          sessionsByModule[m.id] = (r.data || []).sort((a, b) => a.order - b.order);
        } catch (e) {
          sessionsByModule[m.id] = [];
        }
      }));
      setModuleSessions(sessionsByModule);
      
      try {
        const capRes = await axios.get(`${API}/capstone/progress`);
        setCapstoneFields(capRes.data?.fields || {});
      } catch (e) { /* ignore */ }
    } catch (err) {
      console.error("Error loading middle-school data:", err);
      setError("Failed to load middle-school content. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isLessonComplete = (lessonId) =>
    progress.some((p) => p.lesson_id === lessonId && p.completed);

  // Aggregate progress across all sessions
  const totalSessions = Object.values(moduleSessions).reduce((sum, ls) => sum + ls.length, 0);
  const completedSessions = Object.values(moduleSessions).reduce(
    (sum, ls) => sum + ls.filter((l) => isLessonComplete(l.id)).length, 0);
  const percent = totalSessions > 0 ? Math.round((completedSessions / totalSessions) * 100) : 0;
  const allDone = totalSessions > 0 && completedSessions === totalSessions;

  const switchToStandard = async () => {
    try {
      await axios.put(`${API}/auth/profile`, { learning_path: "standard" });
      localStorage.setItem("preferred_learning_path", "standard");
    } catch (e) { /* ignore */ }
    // Hard reload so App's user state refreshes with new learning_path
    window.location.href = "/dashboard";
  };

  // Download the student's own Mini Business Plan PDF (auth-protected endpoint)
  const downloadMyBusinessPlan = async () => {
    try {
      const res = await axios.get(`${API}/student/business-plan/pdf`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = ((profile?.name || "Student").split(" ")[0] || "Student").replace(/\s+/g, "_");
      a.download = `${safe}_Business_Plan.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error("Error downloading business plan:", err);
      alert("Couldn't download your Business Plan PDF. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-pink-50 via-yellow-50 to-blue-50">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || modules.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-yellow-50 to-blue-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="container mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md mx-auto text-center">
            <div className="text-5xl mb-4">😕</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('ms.noContentTitle')}</h2>
            <p className="text-gray-600 mb-6">{error || t('ms.noContentBody')}</p>
            <button
              onClick={() => navigate("/dashboard")}
              className="lemon-button px-6 py-2 rounded-lg text-white font-semibold"
              data-testid="ms-back-to-dashboard-btn"
            >
              {t('ms.backToDashboard')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-yellow-50 to-blue-50">
      <Navbar user={user} onLogout={onLogout} />

      <div className="container mx-auto px-4 py-8 max-w-5xl" data-testid="middle-school-dashboard">
        <AnnouncementBanner role={user?.role} />
        {/* Hero Welcome */}
        <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 rounded-3xl shadow-2xl p-6 sm:p-8 mb-8 text-white relative overflow-hidden">
          <div className="absolute -top-6 -right-6 text-9xl opacity-20 select-none">🚀</div>
          <div className="relative flex items-start gap-4 sm:gap-5 flex-wrap">
            {(() => {
              const av = (profile?.avatar_url || "").replace(/^preset:/, "");
              const emoji = AVATAR_EMOJI[av];
              return (
                <button
                  type="button"
                  onClick={() => setShowProfileModal(true)}
                  data-testid="ms-dashboard-avatar-btn"
                  aria-label="Edit your profile"
                  className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center text-4xl sm:text-5xl bg-white/95 hover:bg-white border-2 border-white/40 transition-colors shadow-md shrink-0"
                >
                  <span aria-hidden="true">{emoji || "+"}</span>
                  <span className="absolute -bottom-1 -right-1 bg-white border border-gray-200 rounded-full w-6 h-6 flex items-center justify-center shadow-sm">
                    <Pencil className="w-3 h-3 text-gray-500" aria-hidden="true" />
                  </span>
                </button>
              );
            })()}

            <div className="flex-1 min-w-[220px]">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold mb-2">
                <Sparkles className="w-4 h-4" />
                {t('ms.pathBadge')}
              </div>
              <h1 className="text-3xl sm:text-5xl font-bold mb-2 leading-tight" data-testid="ms-welcome-heading">
                {(profile?.name || "").split(" ")[0]
                  ? t('ms.heyName', { name: (profile?.name || "").split(" ")[0] })
                  : t('ms.heyThere')}
              </h1>
              {profile?.business_name ? (
                <p
                  className="text-base sm:text-lg font-bold opacity-95"
                  data-testid="ms-dashboard-business-name"
                >
                  {t('ms.ceoOf')} <span className="bg-white/25 backdrop-blur-sm px-2 py-0.5 rounded-md">{profile.business_name}</span>
                </p>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowProfileModal(true)}
                  className="text-sm font-bold underline opacity-95 hover:opacity-100"
                  data-testid="ms-dashboard-set-business-name"
                >
                  {t('ms.addBusinessName')}
                </button>
              )}
              {profile?.team_role && TEAM_ROLE_META[profile.team_role] && (() => {
                const meta = TEAM_ROLE_META[profile.team_role];
                const Icon = meta.icon;
                return (
                  <span
                    className="mt-2 inline-flex items-center gap-1 text-xs font-bold bg-white text-purple-700 border border-purple-300 rounded-full px-2.5 py-1"
                    data-testid="ms-dashboard-role-badge"
                  >
                    <Icon className="w-3.5 h-3.5" aria-hidden="true" />
                    {t(meta.labelKey)}
                  </span>
                );
              })()}
              <p className="mt-3 text-sm sm:text-base opacity-95 max-w-2xl">
                {t('ms.tagline')}
              </p>
              {user?.role === "student" && (
                <button
                  type="button"
                  onClick={downloadMyBusinessPlan}
                  className="mt-4 inline-flex items-center gap-2 bg-white text-purple-700 px-4 py-2 rounded-xl font-bold text-sm shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all"
                  data-testid="ms-download-my-plan-btn"
                >
                  <Download className="w-4 h-4" />
                  {t('ms.downloadPlan')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Educator Quick-Share (educators & admins only) */}
        {(user?.role === "educator" || user?.role === "admin") && <EducatorQuickShare />}

        {/* Progress Tracker */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8" data-testid="ms-progress-tracker">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Rocket className="w-6 h-6 text-purple-500" />
              <h2 className="text-xl font-bold text-gray-800">{t('ms.yourProgress')}</h2>
            </div>
            <div className="text-sm font-semibold text-gray-600" data-testid="ms-progress-count">
              {t('ms.sessionsCompleteCount', { done: completedSessions, total: totalSessions })}
            </div>
          </div>
          <div
            className="w-full bg-gray-100 rounded-full h-4 overflow-hidden"
            role="progressbar"
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Overall progress ${percent}%`}
            data-testid="ms-dashboard-progress-bar"
          >
            <div
              className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 transition-all duration-500"
              style={{ width: `${percent}%` }}
              data-testid="ms-progress-bar"
            />
          </div>
          <div className="text-center mt-3 text-2xl font-bold text-purple-600">{percent}%</div>
        </div>

        {/* Youth CEO Camp Final Challenge — visible to students only */}
        {user?.role === "student" && (
          <div className="bg-gradient-to-br from-[#0B1E3F] to-[#132A54] text-white rounded-3xl shadow-xl p-6 sm:p-8 mb-8 relative overflow-hidden" data-testid="ms-dashboard-ceo-challenge-tile">
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#FFD500]/25 blur-3xl" aria-hidden="true" />
            <div className="absolute -bottom-20 -left-16 w-52 h-52 rounded-full bg-[#7C3AED]/20 blur-3xl" aria-hidden="true" />
            <div className="relative flex flex-col md:flex-row items-start md:items-center gap-5">
              <div className="w-16 h-16 rounded-2xl bg-[#FFD500] text-black flex items-center justify-center text-3xl font-black shadow-lg shrink-0" aria-hidden="true">
                🏆
              </div>
              <div className="flex-1">
                <p className="text-[11px] font-black uppercase tracking-widest text-[#FFD500] mb-1">Youth CEO Camp</p>
                <h3 className="text-xl sm:text-2xl font-black leading-tight">Final Challenge — Are You Pitch Day Ready?</h3>
                <p className="mt-1.5 text-sm text-white/70">
                  20-question quiz on entrepreneurship, branding, money, pitching &amp; confidence. Earn the CEO Camp Graduate badge + certificate.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                <button
                  onClick={() => navigate("/ceo-challenge")}
                  data-testid="ms-dashboard-ceo-challenge-start"
                  className="inline-flex items-center justify-center gap-2 bg-[#FFD500] text-black font-black rounded-full px-5 py-3 hover:bg-white transition-colors"
                >
                  Start Challenge →
                </button>
                <button
                  onClick={() => navigate("/ceo-challenge/leaderboard")}
                  data-testid="ms-dashboard-ceo-challenge-leaderboard"
                  className="inline-flex items-center justify-center gap-2 bg-white/10 ring-1 ring-white/25 text-white text-sm font-bold rounded-full px-4 py-3 hover:bg-white/15 transition-colors"
                >
                  Leaderboard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Student Journey Map / Mini Business Plan v1 — students only */}
        {user?.role === "student" && <StudentJourneyMap />}

        {/* CTE Profile Report — students only, surfaces after they engage with Module 1 */}
        {user?.role === "student" && <CTEProfileReport />}

        {/* All 7 Modules with sessions */}
        <div className="space-y-6 mb-8" data-testid="ms-modules-list">
          {modules.map((mod) => {
            const sessions = moduleSessions[mod.id] || [];
            const modCompleted = sessions.filter((s) => isLessonComplete(s.id)).length;
            const modPct = sessions.length > 0 ? Math.round((modCompleted / sessions.length) * 100) : 0;
            const capstoneVal = mod.capstone_field ? capstoneFields[mod.capstone_field] : null;
            return (
              <div
                key={mod.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden"
                data-testid={`ms-module-${mod.order}`}
              >
                <div className="bg-gradient-to-r from-yellow-300 to-orange-300 px-6 py-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                      <Lightbulb className="w-7 h-7 text-orange-700" />
                      <div>
                        <div className="text-xs uppercase tracking-wide font-semibold text-orange-800">
                          {t('ms.moduleLabel', { n: mod.order })}
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">{mod.title}</h2>
                      </div>
                    </div>
                    <div className="text-xs font-bold text-orange-800 bg-white/80 px-2 py-1 rounded-full">
                      {modCompleted}/{sessions.length} · {modPct}%
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  {mod.description && (
                    <p className="text-gray-600 text-sm mb-4">{mod.description}</p>
                  )}
                  <div className="space-y-2" data-testid={`ms-sessions-${mod.order}`}>
                    {sessions.map((s, sIdx) => {
                      const done = isLessonComplete(s.id);
                      const sessionNum = `${mod.order}.${sIdx + 1}`;
                      return (
                        <button
                          key={s.id}
                          onClick={() => navigate(`/middle-school/lessons/${s.id}`)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all hover:shadow-md ${
                            done ? "bg-green-50 border-green-300" : "bg-white border-gray-200 hover:border-purple-400"
                          }`}
                          data-testid={`ms-session-${sessionNum.replace(".","-")}`}
                        >
                          <div className="text-2xl shrink-0">{done ? "✅" : "📘"}</div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-bold text-gray-500 mb-0.5">{t('ms.sessionLabel', { n: sessionNum })}</div>
                            <h3 className="font-semibold text-gray-800 truncate">{s.title}</h3>
                          </div>
                          {done ? (
                            <CheckCircle2 className="w-6 h-6 text-green-500 shrink-0" />
                          ) : (
                            <ArrowRight className="w-5 h-5 text-purple-500 shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {capstoneVal && (
                    <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-sm">
                      <div className="text-xs font-bold uppercase tracking-wide text-orange-700 mb-0.5">
                        {mod.capstone_label || t('ms.capstoneLabel')}
                      </div>
                      <div className="text-gray-700 italic whitespace-pre-wrap" data-testid={`ms-capstone-m${mod.order}`}>
                        {capstoneVal}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Celebration banner */}
        {allDone && (
          <div className="bg-gradient-to-r from-green-400 to-emerald-500 rounded-2xl shadow-2xl p-8 text-white text-center mb-8" data-testid="ms-completion-banner">
            <div className="text-6xl mb-3">🎉</div>
            <h2 className="text-3xl font-bold mb-2">{t('ms.celebrationTitle')}</h2>
            <p className="text-lg opacity-95">
              {t('ms.celebrationBody')}
            </p>
          </div>
        )}

        {/* Switch path link */}
        <div className="text-center">
          <button
            onClick={switchToStandard}
            className="text-sm text-gray-500 hover:text-purple-600 underline"
            data-testid="ms-switch-to-standard-btn"
          >
            {t('ms.switchToStandard')}
          </button>
        </div>
      </div>
      <LemonBotFloater activity="middle_school_dashboard" />

      {/* Personalization Phase 1: Student Profile editor */}
      <StudentProfileModal
        open={showProfileModal}
        user={profile}
        onClose={() => setShowProfileModal(false)}
        onSaved={(updated) => setProfile((p) => ({ ...(p || {}), ...updated }))}
      />
    </div>
  );
}

export default MiddleSchoolDashboard;
